import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import HaulCard from "../components/HaulCard";
import { Compass, Loader2, RefreshCw, Flame, UserPlus, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function DiscoverPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("foryou");
  const [tabInitialized, setTabInitialized] = useState(false);
  const [hauls, setHauls] = useState([]);
  const [trending, setTrending] = useState([]);
  const [followingPosts, setFollowingPosts] = useState([]);
  const [followingCount, setFollowingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchFeed = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const tasks = [
        api.get("/hauls/discover?limit=20"),
        api.get("/hauls/trending?limit=3&window_days=7").catch(() => ({ data: { posts: [] } })),
      ];
      if (user) {
        tasks.push(api.get("/hauls/following?limit=20").catch(() => ({ data: { posts: [], following_count: 0 } })));
      }
      const [feedRes, trendingRes, followingRes] = await Promise.all(tasks);
      setHauls(feedRes.data.posts || []);
      setTrending(trendingRes.data.posts || []);
      if (followingRes) {
        setFollowingPosts(followingRes.data.posts || []);
        setFollowingCount(followingRes.data.following_count || 0);
        // Default to Following tab on first load only (if user follows anyone)
        if (!tabInitialized) {
          setTabInitialized(true);
          if ((followingRes.data.following_count || 0) > 0) setTab("following");
        }
      } else if (!tabInitialized) {
        setTabInitialized(true);
      }
    } catch {
      setHauls([]);
      setTrending([]);
      setFollowingPosts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const trendingIds = new Set(trending.map((t) => t.post_id));
  const restHauls = hauls.filter((h) => !trendingIds.has(h.post_id));

  return (
    <div data-testid="discover-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 flex items-center gap-3">
            <Compass className="w-7 h-7 text-rose-500" />
            Discover
          </h1>
          <p className="text-xs sm:text-sm text-zinc-500 mt-1">Latest hauls from the collector community</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchFeed(true)}
          disabled={refreshing}
          data-testid="discover-refresh-btn"
          className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-rose-500/40"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1.5 hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="discover-tabs" className="grid grid-cols-2 bg-zinc-900 border border-zinc-800 mb-5 h-11">
            <TabsTrigger
              data-testid="tab-following"
              value="following"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100 text-zinc-400 gap-1.5"
            >
              <Users className="w-3.5 h-3.5" /> Following
              {followingCount > 0 && <span className="ml-1 text-[10px] text-rose-400 font-mono">({followingCount})</span>}
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-foryou"
              value="foryou"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100 text-zinc-400 gap-1.5"
            >
              <Flame className="w-3.5 h-3.5" /> For You
            </TabsTrigger>
          </TabsList>

          <TabsContent value="following">
            {!user ? (
              <div data-testid="following-needs-auth" className="text-center py-16 rounded-2xl bg-zinc-900/40 border border-dashed border-zinc-800">
                <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-300 text-sm">Sign in to see hauls from collectors you follow.</p>
                <Link to="/login" className="mt-3 inline-block text-rose-500 text-sm hover:text-rose-400 font-medium">Sign in &rarr;</Link>
              </div>
            ) : followingCount === 0 ? (
              <div data-testid="following-empty-no-followees" className="text-center py-16 rounded-2xl bg-gradient-to-br from-zinc-900/60 to-rose-950/10 border border-dashed border-rose-500/20">
                <UserPlus className="w-10 h-10 text-rose-500/60 mx-auto mb-3" />
                <p className="text-zinc-200 text-sm font-medium">Your Following feed is empty</p>
                <p className="text-xs text-zinc-500 mt-1 max-w-sm mx-auto">Find collectors to follow in the For You tab, or tap their name on any haul to visit their profile.</p>
                <Button size="sm" onClick={() => setTab("foryou")} className="mt-4 bg-rose-600 hover:bg-rose-500 text-white" data-testid="explore-foryou-btn">
                  Explore For You
                </Button>
              </div>
            ) : followingPosts.length === 0 ? (
              <div data-testid="following-empty-no-posts" className="text-center py-16 rounded-2xl bg-zinc-900/40 border border-dashed border-zinc-800">
                <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm">No hauls yet from the {followingCount} {followingCount === 1 ? "collector" : "collectors"} you follow.</p>
                <p className="text-[11px] text-zinc-600 mt-1">Check back soon, or discover more in For You.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="following-feed">
                {followingPosts.map((h) => <HaulCard key={h.post_id} haul={h} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="foryou">
            {hauls.length === 0 && trending.length === 0 ? (
              <div data-testid="discover-empty" className="text-center py-16 rounded-2xl bg-zinc-900/40 border border-dashed border-zinc-800">
                <Compass className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm">No hauls yet. Be the first to post from your Scan Recap!</p>
                <p className="text-[11px] text-zinc-600 mt-1">Your public profile needs to be enabled in Profile settings.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {trending.length > 0 && (
                  <section data-testid="trending-section">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="relative">
                        <Flame className="w-5 h-5 text-amber-400" />
                        <div className="absolute inset-0 animate-ping opacity-40">
                          <Flame className="w-5 h-5 text-amber-500" />
                        </div>
                      </div>
                      <h2 className="font-heading text-lg font-semibold text-zinc-100">Hot this week</h2>
                      <span className="text-[10px] text-amber-400/70 font-mono uppercase tracking-wider bg-amber-500/10 border border-amber-500/20 rounded-full px-2 py-0.5 ml-1">
                        Top {trending.length}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {trending.map((h, idx) => (
                        <div key={h.post_id} data-testid={`trending-rank-${idx + 1}`} className="relative">
                          <div className="absolute -left-2 -top-2 sm:-left-3 sm:-top-3 z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-amber-500 to-rose-500 text-black font-heading text-sm font-bold flex items-center justify-center shadow-lg shadow-amber-500/20 ring-2 ring-zinc-950">
                            {idx + 1}
                          </div>
                          <HaulCard haul={h} />
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                {restHauls.length > 0 && (
                  <section data-testid="latest-section">
                    {trending.length > 0 && (
                      <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-3">Latest</h2>
                    )}
                    <div className="space-y-4">
                      {restHauls.map((h) => <HaulCard key={h.post_id} haul={h} />)}
                    </div>
                  </section>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
